import Close from "@mui/icons-material/Close";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Delete from "@mui/icons-material/Delete";
import Key from "@mui/icons-material/Key";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { hostname, parseError } from "../../../utils";

interface PersonalAccessKey {
  id: string;
  name: string;
  keyPrefix: string;
  lastUsed: string | null;
  expiresAt: string | null;
  isActive: boolean;
  createdDate: string | null;
}

interface PersonalAccessKeysDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  eventListener: Function;
}

export default function PersonalAccessKeysDialog({
  open,
  onClose,
  getToken,
  eventListener,
}: PersonalAccessKeysDialogProps) {
  const [keys, setKeys] = useState<PersonalAccessKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [showNewKey, setShowNewKey] = useState(false);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/users/personal-access-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKeys(response.data.keys || []);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newKeyName.trim()) {
      eventListener({
        type: "SET_ERROR",
        error: "Please enter a name for the key",
      });
      return;
    }

    setCreating(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/users/personal-access-keys`,
        { name: newKeyName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Store the newly created key to display it
      setNewlyCreatedKey(response.data.key);
      setShowNewKey(true);
      setNewKeyName("");
      setShowCreateForm(false);

      // Refresh the list
      await fetchKeys();

      eventListener({
        type: "SET_SUCCESS",
        message: "Personal access key created successfully",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (keyId: string) => {
    setDeleting(keyId);
    try {
      const token = await getToken();
      await axios.delete(`${hostname}/users/personal-access-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Refresh the list
      await fetchKeys();

      eventListener({
        type: "SET_SUCCESS",
        message: "Personal access key deleted",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleCopyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      eventListener({
        type: "SET_SUCCESS",
        message: "Key copied to clipboard",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: "Failed to copy key to clipboard",
      });
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (open) {
      fetchKeys();
      // Reset state when dialog opens
      setNewlyCreatedKey(null);
      setShowNewKey(false);
      setShowCreateForm(false);
      setNewKeyName("");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Key />
          Personal Access Keys
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Personal access keys can be used to authenticate against the API. Unlike
          company API keys, personal access keys give access to all companies you
          have access to.
        </Typography>

        {/* Newly created key alert */}
        {newlyCreatedKey && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <IconButton
                  size="small"
                  onClick={() => setShowNewKey(!showNewKey)}
                >
                  {showNewKey ? <VisibilityOff /> : <Visibility />}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleCopyKey(newlyCreatedKey)}
                >
                  <ContentCopy />
                </IconButton>
                <IconButton size="small" onClick={() => setNewlyCreatedKey(null)}>
                  <Close />
                </IconButton>
              </Box>
            }
          >
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
              Save this key now - you won't be able to see it again!
            </Typography>
            <Box
              sx={{
                fontFamily: "monospace",
                fontSize: "0.9rem",
                wordBreak: "break-all",
                bgcolor: "action.hover",
                p: 1,
                borderRadius: 1,
              }}
            >
              {showNewKey ? newlyCreatedKey : "••••••••••••••••••••••••••••••••"}
            </Box>
          </Alert>
        )}

        {/* Create new key form */}
        {showCreateForm ? (
          <Paper sx={{ p: 2, mb: 2, bgcolor: "action.hover" }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Create New Key
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <TextField
                size="small"
                label="Key Name"
                placeholder="e.g., Development, Production"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleCreate();
                  }
                }}
                fullWidth
                autoFocus
                helperText="A descriptive name to identify this key"
              />
              <LoadingButton
                variant="contained"
                onClick={handleCreate}
                loading={creating}
                sx={{ minWidth: 80 }}
              >
                Create
              </LoadingButton>
              <Button
                onClick={() => {
                  setShowCreateForm(false);
                  setNewKeyName("");
                }}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        ) : (
          <Button
            variant="outlined"
            onClick={() => setShowCreateForm(true)}
            disabled={keys.length >= 10}
            sx={{ mb: 2 }}
          >
            Create New Key
          </Button>
        )}

        {keys.length >= 10 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have reached the maximum of 10 personal access keys.
          </Alert>
        )}

        {/* Keys table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : keys.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No personal access keys yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                keys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>{key.name}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {key.keyPrefix}••••••••
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(key.createdDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(key.lastUsed)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={key.isActive ? "Active" : "Revoked"}
                        color={key.isActive ? "primary" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Delete key">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(key.id)}
                          disabled={deleting === key.id}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
